import {
  Box,
  Button,
  Grid,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { icons } from "assets/AssetHelper";
import { useThemePalette } from "common/hooks/theme_palette";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getPresignedUrlRequest } from "services/modules/uploadFiles";
import setAuthToken from "services/modules/utils/handel_api";
import CloseIcon from "@mui/icons-material/Close";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import CFormErrors from "./CFormErrors";

function CMultiUpload({
  type,
  id,
  label,
  name,
  formik,
  changeAction,
  text,
  enableVideo = false,
}) {
  const {
    load: { loading },
  } = useSelector((state) => state?.files);

  const [fileUrl, setFileUrl] = useState([]);
  const [openLoader, setOpenLoader] = useState(false);
  const { orange, textGray, oliveGreen } = useThemePalette();
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  useEffect(() => {
    formik?.values?.images && setFileUrl(formik?.values?.images);
  }, [formik?.values?.images]);

  const handleClose = (index) => {
    let filterdList = fileUrl?.filter((val, idx) => idx !== index);
    setFileUrl(filterdList);
    changeAction(filterdList);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    // Handle drag leave if needed
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      uploadFile(files[0], type);
    }
  };

  useEffect(() => {
    setError(formik?.errors[name]);
  }, [formik?.errors, name]);

  const uploadFile = async (file, type) => {
    const imageExe = ["png", "jpg", "jpeg"];
    const videoExe = ["mp4", "mov", "avi", "wmv", "flv", "webm", "mkv"];
    const allowedExe = enableVideo ? [...imageExe, ...videoExe] : imageExe;
    const extension = file?.type.slice(file?.type.indexOf("/") + 1);
    const isVideo = enableVideo && videoExe.includes(extension);
    const maxSize = isVideo ? 100 * 1024 * 1024 : 10 * 1024 * 1024; // 100MB for videos, 10MB for images

    try {
      if (file.size > maxSize) {
        const sizeLimit = isVideo ? "100MB" : "10MB";
        setError(`file size must be less than ${sizeLimit}`);
        return;
      }
      if (!allowedExe?.includes(extension)) {
        const errorMessage = enableVideo
          ? "file must be png, jpg, jpeg, mp4, mov, avi, wmv, flv, webm, or mkv"
          : "file must be png, jpg, or jpeg";
        setError(errorMessage);
        return;
      }
      setError(null);
      setOpenLoader(true);

      const requestData = {
        body: {
          files: [
            {
              extension: extension,
            },
          ],
        },
        file: file,
        updateData: (data) => {
          changeAction([...fileUrl, data]);
          setFileUrl((prevData) => [...prevData, data]);
        },
        action: (data) => {
          setOpenLoader(false);
          const token = localStorage.getItem("token");
          setAuthToken(token);
        },
      };
      dispatch(getPresignedUrlRequest({ ...requestData }));
    } catch (error) {
      console.error("Error in uploadFile:", error);
    }
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadFile(file, type);
    }
  };

  return (
    <Grid container gap={2} alignItems="center" key={name}>
      <Stack>
        <Stack sx={{ mb: "15px" }}>
          <Typography
            sx={{
              fontFamily: "Cairo",
              fontSize: "32px",
              fontWeight: "400",
            }}
          >
            {label}
          </Typography>
        </Stack>
        <Box
          sx={{
            border: "1px dashed rgba(0, 0, 0, 0.25)",
            borderRadius: "10px",
            height: "168px",
            width: "168px",
            mr: "14px",
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <label htmlFor={`multiUpload-${id}`}>
            <input
              id={`multiUpload-${id}`}
              name={`multiUpload-${id}`}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />
            <Stack alignItems="center" rowGap={"5px"} py="2rem">
              {loading && openLoader ? (
                <CircularProgress sx={{ color: oliveGreen }} />
              ) : (
                <>
                  <img
                    src={icons?.uploadIcon}
                    style={{ width: "31.17px", height: "25.5px" }}
                    alt="upload icon"
                  />
                  <Typography fontSize="12px" textAlign="center">
                    {enableVideo
                      ? "Select an image or video file or drag and drop here"
                      : "Select an image file or drag and drop here"}
                  </Typography>
                  {text && (
                    <Typography fontSize="11px" color={textGray}>
                      {text}
                    </Typography>
                  )}
                  <Button
                    component="span"
                    sx={{
                      color: orange,
                      border: `1px solid ${orange}`,
                      fontSize: "12px",
                      px: "14px",
                      py: "3px !important",
                    }}
                  >
                    {enableVideo ? "Select Media" : "Select File"}
                  </Button>
                </>
              )}
            </Stack>
          </label>
        </Box>
        <CFormErrors errorsMsg={error} />
      </Stack>
      {fileUrl?.length > 0 &&
        fileUrl?.map((data, index) => {
          const isVideoFile =
            enableVideo &&
            (data.includes(".mp4") ||
              data.includes(".mov") ||
              data.includes(".avi") ||
              data.includes(".wmv") ||
              data.includes(".flv") ||
              data.includes(".webm") ||
              data.includes(".mkv") ||
              data.type?.includes("video"));

          return (
            <Box
              key={index}
              item
              // xs={3}
              sx={{
                border: " 1px dashed rgba(0, 0, 0, 0.25)",
                borderRadius: "10px",
                position: "relative",
                height: "168px !important",
                width: "168px !important",
                mt: "auto",
                mr: "14px",
              }}
            >
              {isVideoFile ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "#f5f5f5",
                    position: "relative",
                  }}
                >
                  <VideoFileIcon
                    sx={{
                      fontSize: "48px",
                      color: "#666",
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                  <PlayArrowIcon
                    sx={{
                      fontSize: "24px",
                      color: orange,
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      ml: "8px",
                    }}
                  />
                </Box>
              ) : (
                <Box
                  component="img"
                  src={data}
                  alt="img"
                  width="100%"
                  height={"100%"}
                  sx={{
                    borderRadius: "10px",
                  }}
                />
              )}
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "48px",
                  height: "48px",
                  bgcolor: "white",
                  borderRadius: "50%",
                  filter: "drop-shadow(0px 0px 4px rgba(185, 185, 185, 0.25))",
                  transform: "translate(50%, -30%)",
                  cursor: "pointer",
                }}
                onClick={() => handleClose(index)}
              >
                <CloseIcon />
              </Stack>
            </Box>
          );
        })}
    </Grid>
  );
}

export default CMultiUpload;
